import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { EVChargingCalculator, SmartChargeTimeline } from '../RichCalculators';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function OpladningTeslaModel3({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad koster det at oplade en Tesla Model 3 hjemme?', answer: 'Tesla Model 3 bruger ca. 14–16 kWh/100 km i praksis. Ved 15.000 km/år og 90% hjemmeopladning er det ca. 1.900–2.200 kWh. Med spotpris uden tillæg, Saving Mode og 10 øre/kWh laderabat kan den årlige opladning komme ned omkring 1.800–2.800 kr. afhængigt af spotpriser og kørsel.' },
    { question: 'Hvor hurtigt kan Tesla Model 3 lade hjemme?', answer: 'Tesla Model 3 lader hjemme (AC) med op til 11 kW, hvilket de fleste 22 kW ladebokse let leverer. Det giver typisk en fuld opladning natten over. På Supercharger (DC) kan den lade med op til 170–250 kW, men hjemmeopladning er langt billigere.' },
    { question: 'Hvilken ladeboks passer til Tesla Model 3?', answer: 'Alle vores ladebokse passer til Tesla Model 3, da den bruger Type 2-stik (standard i Europa). NexBlue Edge 2 (3.499 kr.) er det billigste premium-valg, Zaptec Go 2 (5.999 kr.) er mest fremtidssikret med V2G. Begge inkluderer Saving Mode og serviceaftale.' },
    { question: 'Understøtter Tesla Model 3 V2G?', answer: 'Nej, ikke endnu. Tesla har på nuværende tidspunkt ikke annonceret V2G (tovejsopladning) til Model 3 eller Model Y. Vælger du alligevel en V2G-ready boks som Zaptec Go 2, er du klar, hvis Tesla og dansk lovgivning åbner for det i fremtiden.' },
    { question: 'Kan jeg bruge Saving Mode med min Tesla Model 3?', answer: 'Ja – Tesla er blandt de bilmærker, vi har bekræftet. Saving Mode fungerer gennem både din ladeboks (Zaptec, NexBlue eller Easee), din bil og enkel-appen. Den planlægger automatisk opladningen til de billigste timer. Du får også 10 øre/kWh laderabat oveni. Se listen over bekræftede bilmærker på /understoettede-bilmaerker.' },
    { question: 'Er det billigere at oplade Tesla hjemme end på Supercharger?', answer: 'Ja, markant. Hjemmeopladning til spotpris er typisk en brøkdel af prisen på Supercharger. Med Saving Mode og laderabat hos enkel bliver forskellen endnu større. Brug Supercharger til lange ture og hjemmeladning til hverdagen.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'forbrug', label: 'Forbrug & rækkevidde' },
    { id: 'hjemme', label: 'Oplad hjemme billigst' },
    { id: 'beregner', label: 'Beregn opladning' },
    { id: 'boks', label: 'Ladeboks til Model 3' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Opladning af Tesla Model 3', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Opladning af Tesla Model 3 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Elbil-guide · Tesla Model 3 · 2026"
        h1="Opladning af Tesla Model 3 i 2026 – pris, ladeboks og besparelse"
        intro="Alt om at lade din Tesla Model 3 hjemme: forbrug, ladehastighed, hvad det koster om året – og hvordan du gør opladningen så billig som muligt med spotpris uden tillæg, Saving Mode og laderabat."
        badges={['11 kW hjemmeladning', 'Type 2-stik', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1617704548623-340376564e68?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '~14–16 kWh', label: 'forbrug pr. 100 km (praksis)' },
          { value: '11 kW', label: 'hjemmeladning (AC)' },
          { value: '~1.800–2.800 kr.', label: 'opladning/år ved 15.000 km' },
          { value: '10 øre/kWh', label: 'laderabat hos enkel' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad koster det at oplade en Tesla Model 3?">
        <AnswerBox question="Hvad koster opladning af Tesla Model 3 hjemme i 2026?">
          Tesla Model 3 bruger ca. <strong>14–16 kWh/100 km</strong> i praksis. Ved 15.000 km/år og 90% hjemmeopladning bliver det
          ca. <strong>1.900–2.200 kWh</strong>. Med spotpris uden tillæg, Saving Mode og 10 øre/kWh laderabat kan den årlige opladning
          komme ned omkring <strong>1.800–2.800 kr.</strong> – langt billigere end Supercharger (DC), der koster mange gange mere pr. kWh.
        </AnswerBox>
        <p>
          Tesla Model 3 er en af Danmarks mest solgte elbiler, og dens relativt lave forbrug gør den billig i drift – især hvis du lader hjemme
          i de rigtige timer. Hjemmeopladning (AC) er nemlig altid markant billigere end offentlig hurtigladning.
        </p>
      </ProseSection>

      <ProseSection id="forbrug" heading="Tesla Model 3 – forbrug, batteri og rækkevidde" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Nøgletal for Tesla Model 3 (Highland)"
            columns={[{ name: 'Tesla Model 3', highlight: true }]}
            rows={[
              { label: 'Reelt forbrug', values: ['ca. 14–16 kWh/100 km'] },
              { label: 'Batteri (RWD)', values: ['ca. 60–64 kWh brugbart'] },
              { label: 'Reel rækkevidde', values: ['ca. 400–550 km (afhænger af variant og vejr)'] },
              { label: 'Hjemmeladning (AC)', values: ['op til 11 kW'] },
              { label: 'Hurtigladning (DC)', values: ['op til 170–250 kW'] },
              { label: 'Stiktype', values: ['Type 2 (AC) / CCS (DC)'] },
            ]}
          />
        </div>
        <p>Tallene varierer mellem varianter og med kørestil, dæk og temperatur. Om vinteren stiger forbruget typisk 10–25%.</p>
      </ProseSection>

      <ProseSection id="hjemme" heading="Sådan lader du Model 3 billigst hjemme">
        <p>
          Hemmeligheden bag billig opladning er at lade i de billige timer – typisk om natten. Saving Mode gør det automatisk: den analyserer
          spotprisen time for time og placerer opladningen, hvor strømmen er billigst, så bilen er klar, når du skal bruge den.
        </p>
        <div className="my-6"><SmartChargeTimeline /></div>
        <p>
          Oveni får du 10 øre/kWh laderabat på de kWh, du lader via Saving Mode i enkel-appen (kræver kompatibel ladeboks og bil), og opladningen sker til spotpris uden tillæg.
          Du gør ingenting – det hele kører automatisk via enkel-appen og din ladeboks.
        </p>
      </ProseSection>

      <ProseSection id="beregner" heading="Beregn din Tesla Model 3-opladning" alt>
        <div className="my-2"><EVChargingCalculator defaultKm={15000} kwhPer100km={15} title="Beregn opladning af din Tesla Model 3" /></div>
      </ProseSection>

      <ProseSection id="boks" heading="Hvilken ladeboks passer til Tesla Model 3?">
        <p>Tesla Model 3 bruger Type 2-stik, så alle vores ladebokse passer. Vælg ud fra budget og fremtidssikring:</p>
        <div className="grid sm:grid-cols-2 gap-4 my-6">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="Billigste premium-valg" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go_2} note="Mest fremtidssikret · V2G" />
        </div>
        <p>Begge leverer 22 kW (Model 3 trækker op til 11 kW AC) og inkluderer Saving Mode og serviceaftale til 0 kr./md. hos enkel energi.</p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/elbil', label: 'Elbil-løsning' },
        { path: '/portal/smart-charge', label: 'Saving Mode' },
        { path: '/el-fordele#laderabat', label: 'Laderabat' },
        { path: '/sammenlign/ladeboks-installation', label: 'Installation af ladeboks' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Lad din Tesla billigst hjemme" subtext="Spotpris uden tillæg, Saving Mode og laderabat. Ladeboks fra 3.499 kr." primary={{ path: '/opladere', text: 'Se ladebokse' }} secondary={{ path: '/elaftale', text: 'Se el-aftaler' }} />
      <SEODisclaimer page={page} />
    </article>
  );
}